// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Button, Card, CardContent, LinearProgress, Box, ButtonGroup, Stack, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Grid, Divider, TableSortLabel, Link } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import { useLocations } from '../hooks/useLocations';
import ExportToExcel from '../components/ExportToExcel';
import { getMachines, getReports, updateUptimePercentage, getUptimePercentagesByLocation } from '../services/supabaseService';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import DnsIcon from '@mui/icons-material/Dns';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import TimelineIcon from '@mui/icons-material/Timeline';

const SectionHeader = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(4),
  background: theme.palette.mode === 'dark' ? '#424242' : theme.palette.primary.main,
  color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.primary.contrastText,
  borderRadius: theme.shape.borderRadius,
}));

const Status: React.FC = () => {
  const [machines, setMachines] = useState([]);
  const [reportsArray, setReportsArray] = useState([]);
  const [uptimePercentageByLocation, setUptimePercentageByLocation] = useState({});
  const [overallUptimePercentage, setOverallUptimePercentage] = useState(0);
  const [loading, setLoading] = useState(true);
  const locations = useLocations();
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportDetailModalOpen, setReportDetailModalOpen] = useState(false);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('uptimePercentage');
  const theme = useTheme();

  const tableHeadStyle = theme.palette.mode === 'dark' ? {
    backgroundColor: '#555555',
    '& .MuiTableCell-head': {
      color: '#ffffff',
    }
  } : {};

  useEffect(() => {
    const fetchData = async () => {
      try {
        const reportsData = await getReports();
        setReportsArray(reportsData);

        const machinesData = await getMachines();
        if (machinesData) {
          setMachines(machinesData);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (!loading) {
      const calculateUptime = async () => {
        try {
          const currentYear = new Date().getFullYear();
          const uptimePercentages = {};
          const totalWorkDays = [...Array(252).keys()].reduce((workDaysCount, dayOffset) => {
            const day = new Date();
            day.setDate(day.getDate() - dayOffset);

            if (day.getDay() >= 1 && day.getDay() <= 5) {
              workDaysCount++;
            }

            return workDaysCount;
          }, 0);

          machines.forEach(machine => {
            const machineReports = reportsArray.filter(report => report.machine === machine.id);
            let totalDownDays = 0;

            machineReports.forEach(report => {
              const reportStart = new Date(report.date).getTime();
              const reportEnd = report.end_date ? new Date(report.end_date).getTime() : reportStart;
              const yearStart = new Date(currentYear, 0, 1).getTime();
              const yearEnd = new Date(currentYear, 11, 31).getTime();

              if (reportStart < yearEnd && reportEnd > yearStart) {
                const start = Math.max(reportStart, yearStart);
                const end = Math.min(reportEnd, yearEnd);
                totalDownDays += (end - start) / (1000 * 60 * 60 * 24) + 1;
              }
            });

            machine.uptimePercentage = ((totalWorkDays - totalDownDays) / totalWorkDays) * 100;
          });

          for (const location of locations) {
            const locationMachines = machines.filter(machine => machine.location === location);

            if (locationMachines.length > 0) {
              const uptimePercentage = locationMachines.reduce((sum, machine) =>
                sum + machine.uptimePercentage, 0) / locationMachines.length;

              uptimePercentages[location] = uptimePercentage.toFixed(2);

              // Update uptime percentage in Supabase
              await updateUptimePercentage(
                currentYear,
                location,
                'all',
                parseFloat(uptimePercentage.toFixed(2))
              );
            }
          }

          setUptimePercentageByLocation(uptimePercentages);

          const overallUptimePercentage = machines.reduce((sum, machine) =>
            sum + machine.uptimePercentage, 0) / machines.length;

          setOverallUptimePercentage(overallUptimePercentage.toFixed(2));

          // Update overall uptime percentage in Supabase
          await updateUptimePercentage(
            currentYear,
            'all',
            'overall',
            parseFloat(overallUptimePercentage.toFixed(2))
          );
        } catch (error) {
          console.error("Error calculating uptime:", error);
        }
      };

      calculateUptime();
    }
  }, [loading, machines, reportsArray, locations]);

  const handleRowClick = (locationName: string) => {
    setSelectedLocation(locationName);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleReportClick = (report) => {
    setSelectedReport(report);
    setReportDetailModalOpen(true);
  };

  const handleCloseReportDetailModal = () => {
    setReportDetailModalOpen(false);
  };

  // Get reports for a specific machine
  const getReportsForMachine = (machineId) => {
    const machineReports = reportsArray.filter(report => report.machine === machineId);

    // Sort reports by date (newest first)
    return machineReports.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB - dateA;
    });
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortMachines = (machines) => {
    return [...machines].sort((a, b) => {
      if (orderBy === 'name') {
        const nameA = (a.name || a.id).toLowerCase();
        const nameB = (b.name || b.id).toLowerCase();
        return order === 'asc'
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      } else {
        return order === 'asc'
          ? a[orderBy] - b[orderBy]
          : b[orderBy] - a[orderBy];
      }
    });
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  // Get machines for the selected location
  const locationMachines = selectedLocation
    ? machines.filter(machine => machine.location === selectedLocation)
    : [];

  // Sort the machines based on current sort settings
  const sortedLocationMachines = sortMachines(locationMachines);

  return (
    <div>
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" color="text.primary">
          Status
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>
          Monitor real-time machine status and performance metrics across all locations.
        </Typography>
      </Box>
      <SectionHeader elevation={2}>
        <Typography variant="h6">Global Uptime Status</Typography>
      </SectionHeader>
      <Card sx={{ mb: 2, mt: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mr: 2 }}>Overall Uptime</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LinearProgress variant="determinate" value={parseFloat(overallUptimePercentage)} sx={{ flexGrow: 1, mr: 1 }} />
            <Typography variant="body2" color="textSecondary">{`${overallUptimePercentage}%`}</Typography>
          </Box>
        </CardContent>
      </Card>
      <Stack direction="row" justifyContent="center" spacing={2} sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
          <ExportToExcel
            overallUptimePercentage={overallUptimePercentage}
            locations={locations}
            uptimePercentageByLocation={uptimePercentageByLocation}
            currentYear={new Date().getFullYear()}
          />
        </Box>
      </Stack>
      <Grid container spacing={3}>
        {locations.map((locationName) => {
          const uptime = uptimePercentageByLocation[locationName];
          const locMachines = machines.filter(m => m.location === locationName);
          const totalMachines = locMachines.length;
          // Calculate active machines manually for card display if needed, or rely on uptime
          // For simplicity, we'll just show the uptime and total machines here

          let color = theme.palette.error.main;
          if (uptime) {
            if (parseFloat(uptime) > 98) {
              color = theme.palette.success.main;
            } else if (parseFloat(uptime) > 90) {
              color = theme.palette.warning.main;
            }
          }

          return (
            <Grid item xs={12} sm={6} md={4} key={locationName}>
              <Card
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  // borderRadius: 4,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  },
                  position: 'relative',
                  overflow: 'visible'
                }}
                onClick={() => handleRowClick(locationName)}
              >
                <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box' }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <div>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                          {locationName}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" color="text.secondary">
                            {totalMachines} Machines
                          </Typography>
                        </Stack>
                      </div>
                      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                        {/* Visual Circular Progress for Uptime */}
                        <svg width="60" height="60" viewBox="0 0 36 36">
                          <path
                            d="M18 2.0845
                                a 15.9155 15.9155 0 0 1 0 31.831
                                a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke={theme.palette.mode === 'dark' ? '#555' : '#eee'}
                            strokeWidth="3"
                          />
                          <path
                            d="M18 2.0845
                                a 15.9155 15.9155 0 0 1 0 31.831
                                a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke={color}
                            strokeWidth="3"
                            strokeDasharray={`${uptime || 0}, 100`}
                            strokeLinecap="round"
                          />
                        </svg>
                        <Box
                          sx={{
                            top: 0,
                            left: 0,
                            bottom: 0,
                            right: 0,
                            position: 'absolute',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography variant="caption" component="div" color="text.primary" fontWeight="bold">
                            {uptime ? `${Math.round(uptime)}%` : 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Button
                    size="small"
                    endIcon={<SearchIcon />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      color: theme.palette.mode === 'dark' ? '#ffffff !important' : theme.palette.text.primary,
                      justifyContent: 'flex-start',
                      width: 'fit-content'
                    }}
                  >
                    View Details
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Location Details Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle component="div">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {selectedLocation} - Details
            </Typography>
            <IconButton onClick={handleCloseModal}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedLocation && (
            <>
              {/* Summary Stats Cards */}
              <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%', borderRadius: 3, boxShadow: 2 }}>
                    <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                      <Box sx={{
                        mr: 2,
                        p: 1.5,
                        borderRadius: '50%',
                        bgcolor: theme.palette.primary.main + '20',
                        color: theme.palette.primary.main,
                        display: 'flex'
                      }}>
                        <DnsIcon fontSize="large" />
                      </Box>
                      <Box>
                        <Typography variant="h4" fontWeight="bold">
                          {locationMachines.length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" fontWeight="medium">
                          Total Machines
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%', borderRadius: 3, boxShadow: 2 }}>
                    <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                      <Box sx={{
                        mr: 2,
                        p: 1.5,
                        borderRadius: '50%',
                        bgcolor: theme.palette.error.main + '20',
                        color: theme.palette.error.main,
                        display: 'flex'
                      }}>
                        <ReportProblemIcon fontSize="large" />
                      </Box>
                      <Box>
                        <Typography variant="h4" fontWeight="bold">
                          {(() => {
                            const currentYear = new Date().getFullYear();
                            return locationMachines.reduce((total, machine) => {
                              const machineReports = getReportsForMachine(machine.id);
                              return total + machineReports.filter(r => new Date(r.date).getFullYear() === currentYear).length;
                            }, 0);
                          })()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" fontWeight="medium">
                          Total Reports (Current Year)
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%', borderRadius: 3, boxShadow: 2 }}>
                    <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                      <Box sx={{
                        mr: 2,
                        p: 1.5,
                        borderRadius: '50%',
                        bgcolor: theme.palette.success.main + '20',
                        color: theme.palette.success.main,
                        display: 'flex'
                      }}>
                        <TimelineIcon fontSize="large" />
                      </Box>
                      <Box sx={{ width: '100%' }}>
                        <Typography variant="h4" fontWeight="bold">
                          {uptimePercentageByLocation[selectedLocation] || 0}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary" fontWeight="medium">
                          Overall Uptime
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Machines Table - Kept Clean */}
              <SectionHeader elevation={0} sx={{
                mb: 2,
                backgroundColor: 'transparent',
                color: theme.palette.text.primary,
                padding: '0 0 8px 0',
                boxShadow: 'none',
                borderBottom: `1px solid ${theme.palette.divider}`,
                borderRadius: 0
              }}>
                <Typography variant="h6" fontWeight="bold">Machines Status</Typography>
              </SectionHeader>
              <TableContainer component={Paper} elevation={0} sx={{ mb: 4, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
                <Table>
                  <TableHead sx={tableHeadStyle}>
                    <TableRow>
                      <TableCell><Typography fontWeight="bold">Machine Name</Typography></TableCell>
                      <TableCell><Typography fontWeight="bold">Uptime Status</Typography></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedLocationMachines.map((machine) => {
                      const uptimeValue = machine.uptimePercentage;
                      let dotClass = 'dot-red';
                      if (uptimeValue) {
                        if (uptimeValue > 70) dotClass = 'dot-green';
                        else if (uptimeValue >= 50) dotClass = 'dot-orange';
                      }
                      return (
                        <TableRow key={machine.id} hover>
                          <TableCell sx={{ fontWeight: 'medium' }}>{machine.name || machine.id}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', width: 70, mr: 2 }}>
                                <span className={`pulse-dot ${dotClass}`} style={{ marginRight: '8px' }}></span>
                                <Typography variant="body2" fontWeight="bold">
                                  {uptimeValue ? uptimeValue.toFixed(0) : 0}%
                                </Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={uptimeValue || 0}
                                sx={{
                                  flexGrow: 1,
                                  height: 8,
                                  borderRadius: 4,
                                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                                  '& .MuiLinearProgress-bar': {
                                    borderRadius: 4
                                  }
                                }}
                              />
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Consolidated Reports Table */}
              <SectionHeader elevation={0} sx={{
                mb: 2,
                backgroundColor: 'transparent',
                color: theme.palette.text.primary,
                padding: '0 0 8px 0',
                boxShadow: 'none',
                borderBottom: `1px solid ${theme.palette.divider}`,
                borderRadius: 0
              }}>
                <Typography variant="h6" fontWeight="bold">Recent Reports</Typography>
              </SectionHeader>

              <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
                <Table size="small">
                  <TableHead sx={tableHeadStyle}>
                    <TableRow>
                      <TableCell><Typography variant="subtitle2" fontWeight="bold">Date</Typography></TableCell>
                      <TableCell><Typography variant="subtitle2" fontWeight="bold">Machine</Typography></TableCell>
                      <TableCell><Typography variant="subtitle2" fontWeight="bold">Type</Typography></TableCell>
                      <TableCell><Typography variant="subtitle2" fontWeight="bold">Status</Typography></TableCell>
                      <TableCell><Typography variant="subtitle2" fontWeight="bold">Assignee</Typography></TableCell>
                      <TableCell><Typography variant="subtitle2" fontWeight="bold">Description</Typography></TableCell>
                      <TableCell align="center"><Typography variant="subtitle2" fontWeight="bold">Action</Typography></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      // Filter and sort reports for this location (current year only)
                      const currentYear = new Date().getFullYear();
                      const locationMachineIds = locationMachines.map(m => m.id);
                      const locationReports = reportsArray
                        .filter(r => {
                          const reportYear = new Date(r.date).getFullYear();
                          return locationMachineIds.includes(r.machine) && reportYear === currentYear;
                        })
                        .sort((a, b) => new Date(b.date) - new Date(a.date));

                      if (locationReports.length === 0) {
                        return (
                          <TableRow>
                            <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                              <Typography variant="body2" color="text.secondary">No reports found for this location.</Typography>
                            </TableCell>
                          </TableRow>
                        );
                      }

                      return locationReports.map((report) => {
                        const machineName = machines.find(m => m.id === report.machine)?.name || report.machine;
                        const reportData = report.report_data || {};
                        return (
                          <TableRow key={report.id} hover>
                            <TableCell>{new Date(report.date).toLocaleDateString()}</TableCell>
                            <TableCell sx={{ fontWeight: 'medium' }}>{machineName}</TableCell>
                            <TableCell>{report.type || 'N/A'}</TableCell>
                            <TableCell>
                              <Box component="span" sx={{
                                px: 1, py: 0.5, borderRadius: 1, fontSize: '0.75rem', fontWeight: 'bold',
                                bgcolor: report.end_date ? theme.palette.success.main + '20' : theme.palette.warning.main + '20',
                                color: report.end_date ? theme.palette.success.main : theme.palette.warning.main,
                              }}>
                                {report.end_date ? 'Resolved' : 'Active'}
                              </Box>
                            </TableCell>
                            <TableCell>{reportData.assignee || 'N/A'}</TableCell>
                            <TableCell sx={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {reportData.description || 'N/A'}
                            </TableCell>
                            <TableCell align="center">
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleReportClick(report)}
                                sx={{ py: 0, fontSize: '0.75rem', color: theme.palette.mode === 'dark' ? '#ffffff' : undefined }}
                              >
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      });
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Report Detail Modal */}
      <Dialog
        open={reportDetailModalOpen}
        onClose={handleCloseReportDetailModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle component="div">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Report Details
            </Typography>
            <IconButton onClick={handleCloseReportDetailModal}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedReport && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight="bold">Machine</Typography>
                <Typography variant="body1">
                  {machines.find(m => m.id === selectedReport.machine)?.name || selectedReport.machine}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle1" fontWeight="bold">Date</Typography>
                <Typography variant="body1">
                  {new Date(selectedReport.date).toLocaleDateString()}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle1" fontWeight="bold">End Date</Typography>
                <Typography variant="body1">
                  {selectedReport.end_date
                    ? new Date(selectedReport.end_date).toLocaleDateString()
                    : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle1" fontWeight="bold">Type</Typography>
                <Typography variant="body1">{selectedReport.type || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle1" fontWeight="bold">Status</Typography>
                <Typography variant="body1">{selectedReport.end_date ? 'Up' : selectedReport.status || 'Down'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight="bold">Assignee</Typography>
                <Typography variant="body1">
                  {selectedReport.report_data?.assignee || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight="bold">Description</Typography>
                <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                  <Typography variant="body1">
                    {selectedReport.report_data?.description || 'No description provided.'}
                  </Typography>
                </Paper>
              </Grid>
              {selectedReport.report_data?.poNumber && (
                <Grid item xs={6}>
                  <Typography variant="subtitle1" fontWeight="bold">PO Number</Typography>
                  <Typography variant="body1">{selectedReport.report_data.poNumber}</Typography>
                </Grid>
              )}
              {selectedReport.report_data?.serviceNumber && (
                <Grid item xs={6}>
                  <Typography variant="subtitle1" fontWeight="bold">Service Number</Typography>
                  <Typography variant="body1">{selectedReport.report_data.serviceNumber}</Typography>
                </Grid>
              )}
              {selectedReport.report_data?.ticketNumber && (
                <Grid item xs={6}>
                  <Typography variant="subtitle1" fontWeight="bold">Ticket Number</Typography>
                  <Typography variant="body1">
                    <Link href={selectedReport.report_data.ticketNumber} target="_blank" rel="noopener">
                      {selectedReport.report_data.ticketNumber}
                    </Link>
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReportDetailModal}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Status;